import { Pencil, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getClusterById } from '@/services/cluster'

interface ClusterQuickViewModalProps {
  clusterId: string | number
  isOpen: boolean
  onClose: () => void
}

export const ClusterQuickViewModal = ({ clusterId, isOpen, onClose }: ClusterQuickViewModalProps) => {
  const [cluster, setCluster] = useState<any>(null)
  const [associatedModalities, setAssociatedModalities] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && clusterId) {
      fetchClusterData()
    }
  }, [isOpen, clusterId])

  const fetchClusterData = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getClusterById(clusterId.toString())
      setCluster(response.cluster)
      setAssociatedModalities(response.associatedModalities || [])
    } catch (err: any) {
      setError(err.message || 'Failed to fetch cluster details')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 bg-opacity-50 flex items-center justify-center z-50 p-4 bg-black/80'>
      <div className='bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden'>
        {/* Header */}
        <div className='flex items-center justify-between p-4 border-b border-gray-200 bg-curtin-primary-background'>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-primary font-bold'>Cluster quick view</span>
          </div>
          <button onClick={onClose} className='p-1 hover:bg-gray-100 rounded' title='Close'>
            <X className='w-5 h-5 text-gray-500' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6 overflow-y-auto max-h-[calc(90vh-80px)]'>
          {loading ? (
            <div className='text-center py-8'>Loading cluster details...</div>
          ) : error ? (
            <div className='text-red-600 py-8'>Error: {error}</div>
          ) : cluster ? (
            <div className='space-y-6'>
              {/* Title */}
              <div className='flex items-center gap-2'>
                <h2 className='text-xl font-semibold'>{cluster.name}</h2>
                <button className='text-gray-400 hover:text-gray-600'>
                  <Pencil className='h-4 w-4 hover:text-gray-800' />
                  {/* <svg width='16' height='16' viewBox='0 0 16 16' fill='currentColor'>
                    <path d='M8 2L9.5 5.5L13 6L10.5 8.5L11 12L8 10.5L5 12L5.5 8.5L3 6L6.5 5.5L8 2Z' />
                  </svg> */}
                </button>
              </div>

              {/* Summary */}
              <DetailSection title='Summary' content={cluster.description} />
              {cluster.longFormDescriptor && <DetailSection title='Long Form Descriptor' content={cluster.longFormDescriptor} />}
              {cluster.instructionalDesignFeatures && <DetailSection title='Instructional Design Features' content={cluster.instructionalDesignFeatures} />}
              {cluster.empiricalSupport && <DetailSection title='Empirical Support' content={cluster.empiricalSupport} />}
              {cluster.learnerOutcomes && <DetailSection title='Learner Outcomes' content={cluster.learnerOutcomes} />}
              {cluster.suitabilityForDiverseLearners && (
                <DetailSection title='Suitability for Diverse Learners' content={cluster.suitabilityForDiverseLearners} />
              )}
              {cluster.exemplarResearchPaper && <DetailSection title='Exemplar Research Paper' content={cluster.exemplarResearchPaper} />}

              {associatedModalities.length > 0 && (
                <div>
                  <div className='flex items-center gap-2 mb-3'>
                    <h3 className='font-semibold'>Associated Modalities</h3>
                    {/* <Info className='w-4 h-4 text-gray-400' /> */}
                  </div>
                  <div className='bg-blue-50 rounded-lg p-4'>
                    {/* <p className='text-sm text-gray-600 mb-3'>Lorem ipsum is simply dummy text of the printing and typesetting industry.</p> */}
                    <div className='space-y-2'>
                      {associatedModalities.map((modality) => (
                        <div key={modality.id} className='flex items-center gap-2'>
                          {/* <span className='text-blue-600'>%</span> */}
                          <Link to={`/modalities/${modality.id}`} className='font-medium text-blue-700 hover:underline'>
                            {modality.name}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/* Helper component for detail sections */
function DetailSection({ title, content }: { title: string; content: string }) {
  return (
    <div>
      <h3 className='font-semibold mb-2'>{title}</h3>
      <p className='text-gray-700 text-sm leading-relaxed whitespace-pre-wrap'>{content || '—'}</p>
    </div>
  )
}
